import React from "react";
import { Typography, Divider } from "@mui/material";
import { ExternalLink } from "components/navigation";

const StylingGuide = () => (
  <React.Fragment>
    <Typography variant="h5" gutterBottom>
      Styling Guide
    </Typography>
    <Typography gutterBottom>
      For most cases the following approach can be followed:
    </Typography>
    <ul>
      <li>
        Use global theming for changing the default styling of components across
        the entire app, or setting default props. Consider whether a certain
        approach to styling is going to be reused across other stories and use
        variants if applicable.
      </li>
      <li>
        In feature components, prefer using the <code>sx</code> prop. IF an
        individual component within a feature folder will be rendered many times
        (e.g. rendered within a list of items) then that component should use{" "}
        <code>styled()</code>.
      </li>
      <li>
        In the components folder, prefer using the <code>styled()</code> utility
        function.
      </li>
    </ul>
    <Typography mb={3}>
      The different approaches to styling are explained in more detail below.
    </Typography>
    <Typography variant="h6" gutterBottom>
      Theming
    </Typography>
    <Typography gutterBottom>
      MUI allows customisation of the Material UI design system using themes. In
      Cinnamon projects the theme is usually configured across multiple files
      within the theme folder. Theming is a quick and easy way to globally
      configure the overall look and feel of an app.
    </Typography>
    <Typography gutterBottom>
      The theme is a configuration object which allows specifying default values
      for colours, sizing and spacing, etc. See here for more information about
      theming:{" "}
      <ExternalLink href="https://mui.com/material-ui/customization/theming/">
        https://mui.com/material-ui/customization/theming/
      </ExternalLink>
    </Typography>
    <Typography gutterBottom>Use theming when:</Typography>
    <ul>
      <li>
        You want to change global defaults for colours, font sizes, spacing,
        breakpoints
      </li>
      <li>
        You want to change global default props or styles that to change the
        default look or behaviour of MUI components
      </li>
      <li>
        You want to define a{" "}
        <ExternalLink href="https://mui.com/material-ui/customization/theme-components/#creating-new-component-variants">
          variant
        </ExternalLink>{" "}
        for a component - variants are a great way to define a particular styled
        version of a component that needs to be reused in multiple places
      </li>
    </ul>
    <Typography gutterBottom>Avoid theming when:</Typography>
    <ul>
      <li>
        You want to style a one-off instance of a component, or the style is not
        global
      </li>
      <li>You want to style a component that is not part of MUI</li>
    </ul>
    <Typography variant="h6" gutterBottom>
      The sx prop
    </Typography>
    <Typography gutterBottom>
      The sx prop is a quick and easy way to customise a single instance of a
      component. It can be used with all MUI components, it is also available on
      custom components that have been wrapped in <code>styled()</code>. It
      allows styles to be specified on an element in a similar way to the React{" "}
      <code>style</code> prop, but also supports additional functionality, for
      example nested styles, pseudo-classes, or accessing theme variables.
    </Typography>
    <Typography gutterBottom>
      See here for more information:{" "}
      <ExternalLink href="https://mui.com/material-ui/customization/how-to-customize/#the-sx-prop">
        https://mui.com/material-ui/customization/how-to-customize/#the-sx-prop
      </ExternalLink>
    </Typography>
    <Typography gutterBottom>
      The main downside of the sx prop is performance. For components that are
      rendered sparingly, this is generally not an issue. However for components
      that are rendered multiple times on a page, or components that are likely
      to be reused, using <code>styled()</code> is preferable.
    </Typography>
    <Typography gutterBottom>Use sx when:</Typography>
    <ul>
      <li>
        Styling components in a Cinnamon project <code>features</code> folder
      </li>
      <li>You want a one-off customisation of an individual MUI component</li>
      <li>
        You want a one-off customisation of a reusable component created with{" "}
        <code>styled()</code>
      </li>
    </ul>
    <Typography gutterBottom>Avoid sx when:</Typography>
    <ul>
      <li>Performance is critical</li>
      <li>The component customised with the sx prop is rendered many times</li>
      <li>
        The component to be customised is going to be a reusable component and
        used (and possibly customised) elsewhere
      </li>
      <li>
        The style should be applied to all instances of a MUI component (use
        theming)
      </li>
    </ul>
    <Typography variant="h6" gutterBottom>
      styled()
    </Typography>
    <Typography gutterBottom>
      <code>styled()</code> is a utility function which can be used to create
      reusable styled components. It is how MUI components are styled. See{" "}
      <ExternalLink href="https://mui.com/system/styled/">
        https://mui.com/system/styled/
      </ExternalLink>
    </Typography>
    <Typography gutterBottom>
      It is a wrapper around the <code>styled()</code> utility in{" "}
      <ExternalLink href="https://emotion.sh/docs/styled">Emotion</ExternalLink>{" "}
      but adds additional functionality. This includes access to the theme
      variables, as well as the ability to add support for the sx prop to the
      component.
    </Typography>
    <Typography gutterBottom>Use styled() when:</Typography>
    <ul>
      <li>
        Styling components in a Cinnamon project &apos;components&apos; folders
      </li>
      <li>You want to style a reusable component</li>
      <li>
        You want to style a one-off instance of a component and performance is
        important
      </li>
      <li>You want to add support for the sx prop to a styled component</li>
    </ul>
    <Typography gutterBottom>Avoid styled() when:</Typography>
    <ul>
      <li>
        The style should be applied to all instances of a MUI component (use
        theming)
      </li>
    </ul>
    <Typography gutterBottom>
      There are a couple of &apos;gotchas&apos; to be aware of when using
      styled():
    </Typography>
    <ol>
      <li>
        The styled() function passes a <code>className</code> prop to the
        component being styled. It is important to make sure that this prop is
        being handled by the component, and being applied to an element being
        rendered in order for the styling to be applied.
      </li>
      <li>
        Root-level properties returned from styled() are applied to the
        top-level element being styled, any class selectors can only target
        children of the top-level element. So for instance when applying
        styled() to a MUI Box component, which has a class of{" "}
        <code>{`"MuiBox-root"`}</code>, it is not possible to target{" "}
        <code>{`".MuiBox-root"`}</code> as that is the element being styled. Any
        top-level css properties from the styled() object will affect the
        MuiBox-root element however.
      </li>
    </ol>
    <Typography variant="h6" gutterBottom>
      classnames
    </Typography>
    <Typography gutterBottom>
      MUI components can be styled using traditional css classnames or SASS.
      This can be useful when styling very complicated features that need a
      large degree of styling, and including in the components would be
      difficult or add unnecessary complexity. Generally the other forms of
      styling mentioned above should be preferred.
    </Typography>
    <Typography variant="h6" gutterBottom>
      Inline styles
    </Typography>
    <Typography gutterBottom>
      You can also use traditional inline styles{" "}
      <code>{`style={{ fontSize: "1.2em" }}`}</code>, however these should
      always be avoided in reusable components as they are hard to override.
      Generally the other styling options mentioned above should always be
      preferred.
    </Typography>
  </React.Fragment>
);

export default StylingGuide;
